import React, { useState } from "react";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import Dropdown from "../../../components/common/Dropdown";
import Checkbox from "../../../components/common/Checkbox";
import {
  ADMIN_ROLES,
  DEFAULT_PERMISSIONS,
} from "../../../constants/admin.constants";

const AdminCreateModal = ({ onSubmit, branches = [] }) => {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phoneNumber: "",
    password: "",
    role: null,
    rolePreset: null,
    branch: null,
    customPermissions: null,
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showCustomPermissions, setShowCustomPermissions] = useState(false);

  // Convert roles to Dropdown format
  const roleOptions = ADMIN_ROLES.filter((role) => role !== "root").map(
    (role) => ({
      _id: role,
      name: role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, " "),
    })
  );

  // Convert presets to Dropdown format
  const presetOptions = Object.keys(DEFAULT_PERMISSIONS)
    .filter((preset) => preset !== "root")
    .map((preset) => ({
      _id: preset,
      name: preset.charAt(0).toUpperCase() + preset.slice(1),
    }));

  // Add custom option
  presetOptions.push({ _id: "custom", name: "Custom Permissions" });

  // Convert branches to Dropdown format
  const branchOptions = branches.map((branch) => ({
    _id: branch.branchCode || branch.code,
    name: `${branch.name || branch.branchName} (${
      branch.branchCode || branch.code
    })`,
  }));

  const validate = () => {
    let tempErrors = {};
    if (!formData.email) tempErrors.email = "Email is required";
    if (!/\S+@\S+\.\S+/.test(formData.email))
      tempErrors.email = "Email is not valid";
    if (!formData.name) tempErrors.name = "Name is required";
    if (!formData.password) tempErrors.password = "Password is required";
    if (formData.password.length < 6)
      tempErrors.password = "Must be at least 6 characters long";
    if (!formData.role) tempErrors.role = "Role is required";
    if (!formData.branch && formData.role?._id !== "admin")
      tempErrors.branch = "Branch is required for non-admin roles";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleRoleChange = (selectedRole) => {
    // Auto-set rolePreset based on role
    let defaultPreset = null;
    if (selectedRole) {
      const presetId =
        selectedRole._id === "admin"
          ? "admin"
          : selectedRole._id === "branch_manager"
          ? "manager"
          : selectedRole._id === "rate_manager"
          ? "manager"
          : "staff";

      defaultPreset = presetOptions.find((p) => p._id === presetId);
    }

    setFormData((prev) => ({
      ...prev,
      role: selectedRole,
      rolePreset: defaultPreset,
      // Clear branch if admin role is selected
      branch: selectedRole?._id === "admin" ? null : prev.branch,
    }));
  };

  const handlePresetChange = (selectedPreset) => {
    setFormData((prev) => ({
      ...prev,
      rolePreset: selectedPreset,
    }));

    if (selectedPreset?._id === "custom") {
      setShowCustomPermissions(true);
    } else {
      setShowCustomPermissions(false);
      setFormData((prev) => ({
        ...prev,
        customPermissions: null,
      }));
    }
  };

  const handleBranchChange = (selectedBranch) => {
    setFormData((prev) => ({
      ...prev,
      branch: selectedBranch,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      copyCredentials();

      // Convert back to API format
      const submitData = {
        email: formData.email,
        name: formData.name,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        role: formData.role?._id || "staff",
        rolePreset: formData.rolePreset?._id || "staff",
        branchCode: formData.branch?._id || "",
        customPermissions: showCustomPermissions
          ? formData.customPermissions
          : undefined,
      };

      onSubmit(submitData);
    }
  };

  const generatePassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%";
    let password = "";
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({
      ...formData,
      password: password,
    });
  };

  const copyCredentials = () => {
    const credentials = `
Admin Credentials Created:
==========================
Email: ${formData.email}
Name: ${formData.name}
Phone: ${formData.phoneNumber || "Not provided"}
Password: ${formData.password}
Role: ${formData.role?.name || "Not assigned"}
Branch: ${formData.branch?.name || "Not assigned"}
Preset: ${formData.rolePreset?.name || "Not assigned"}
==========================`;
    navigator.clipboard.writeText(credentials);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          placeholder="admin@example.com"
          required
        />
        <Input
          label="Name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          placeholder="Admin Name"
          required
        />
      </div>

      <Input
        label="Phone Number (Optional)"
        type="text"
        name="phoneNumber"
        value={formData.phoneNumber}
        onChange={handleChange}
        error={errors.phoneNumber}
        placeholder="+91 98765 43210"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Dropdown
          label="Role"
          options={roleOptions}
          selected={formData.role}
          onChange={handleRoleChange}
          error={errors.role}
          placeholder="Select Role"
          required
          clearable
        />

        <Dropdown
          label="Branch"
          options={branchOptions}
          selected={formData.branch}
          onChange={handleBranchChange}
          error={errors.branch}
          placeholder="Select Branch"
          // disabled={formData.role?._id === "admin"}
          clearable
        />
      </div>

      <Dropdown
        label="Permission Preset"
        options={presetOptions}
        selected={formData.rolePreset}
        onChange={handlePresetChange}
        placeholder="Select Permission Preset"
        helperText="Choose a preset or select custom to configure manually"
        clearable
      />

      {showCustomPermissions && (
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">
            Custom permissions can be configured after creating the admin
          </p>
        </div>
      )}

      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Password
        </label>
        <div className="flex space-x-3">
          <div className="flex-1">
            <Input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              placeholder="Enter password"
              required
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={generatePassword}
            className="whitespace-nowrap"
          >
            Generate
          </Button>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="show-password"
            checked={showPassword}
            onChange={(e) => setShowPassword(e.target.checked)}
          />
          <label htmlFor="show-password" className="text-sm text-gray-600">
            Show Password
          </label>
        </div>
      </div>

      {formData.password && (
        <div className="p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            Password will be copied to clipboard when you create the admin
          </p>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4">
        <Button
          variant="secondary"
          type="button"
          onClick={() => window.history.back()}
        >
          Cancel
        </Button>
        <Button variant="primary" type="submit">
          Copy Credentials & Create
        </Button>
      </div>
    </form>
  );
};

export default AdminCreateModal;
